//External libs
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from '../common/service';
import NotificationModel from './notification.model';

/**
 * @service NotificationService
 * @description Service for managing push notifications within the application.
 * This service acts as the interface to the `/pushnotification/notification/` backend endpoint.
 * It handles the creation, retrieval, and management of notifications that are exchanged between
 * users (senders and receivers) or generated by system events (e.g., alerts, updates).
 *
 * The service extends the generic `Service` class, inheriting standard CRUD capabilities
 * for the `NotificationModel`.
 *
 * **Key Responsibilities:**
 * 1. **API Integration**: Communicates with the push notification microservice/controller.
 * 2. **Resource Management**: Tracks and cancels ongoing HTTP requests to prevent memory leaks.
 * 3. **Data Abstraction**: Provides a typed interface using `NotificationModel`.
 *
 * @feature 1. Standardized CRUD operations for notifications via inheritance.
 * @feature 2. Robust subscription management for async HTTP calls.
 *
 * @architecture
 * Extends base Service<NotificationModel>.
 * Uses HttpClient for network requests.
 *
 * @service-endpoint /pushnotification/notification/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationService extends Service<NotificationModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/pushnotification/notification/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/pushnotification/notification/');
  }


  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
